import React from 'react';
import SEO from '../components/SEO';

const Terms = () => {
  return (
    <>
      <SEO 
        title="Terms of Service - Energy Spectrum Solar"
        description="Terms of Service for Energy Spectrum. Read our terms and conditions for solar installation, battery storage, and maintenance services in NSW, Australia."
        keywords="terms of service, terms and conditions, solar installation terms, Energy Spectrum, NSW, Australia"
      />
      
      <div className="page-wrapper">
        <div className="container">
          <div className="content-section">
            <h1 className="page-title">Terms of Service</h1>
            <p className="body-text" style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>
              Last Updated: January 2025
            </p>

            <div className="legal-content">
              <section className="legal-section">
                <h2 className="section-title">1. Acceptance of Terms</h2>
                <p className="body-text">
                  By accessing or using the services provided by Energy Spectrum (ABN pending) ("we," "our," or "us"), 
                  you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, 
                  please do not use our services or website.
                </p>
                <p className="body-text">
                  These Terms apply to all services we provide, including solar panel installation, battery storage 
                  systems, system design, maintenance, repairs, and related consulting services in New South Wales, 
                  Australia.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">2. Services Provided</h2>
                <p className="body-text">Energy Spectrum provides the following services:</p>
                <ul className="legal-list">
                  <li><strong>Solar Panel Installation:</strong> Design, supply, and installation of residential and commercial solar photovoltaic systems</li>
                  <li><strong>Battery Storage Solutions:</strong> Installation and integration of battery energy storage systems</li>
                  <li><strong>System Design:</strong> Custom solar system design and engineering services</li>
                  <li><strong>Maintenance and Repairs:</strong> Ongoing maintenance, monitoring, and repair services</li>
                  <li><strong>Consultation:</strong> Energy audits, system assessments, and solar consulting</li>
                  <li><strong>Rebate Assistance:</strong> Guidance and assistance with federal and NSW government solar and battery rebate programs</li>
                </ul>
              </section>

              <section className="legal-section">
                <h2 className="section-title">3. Quotations and Pricing</h2>
                <p className="body-text">
                  All quotations provided by Energy Spectrum are valid for 30 days from the date of issue unless 
                  otherwise specified. Quotations are based on the information provided by the customer and a site 
                  assessment.
                </p>
                <p className="body-text">
                  Prices are subject to change based on:
                </p>
                <ul className="legal-list">
                  <li>Changes to the scope of work requested by the customer</li>
                  <li>Unforeseen site conditions discovered during installation</li>
                  <li>Changes in government rebates or incentives</li>
                  <li>Fluctuations in equipment costs or availability</li>
                  <li>Additional electrical work required to meet safety standards</li>
                </ul>
                <p className="body-text">
                  Any changes to pricing will be communicated to the customer and require approval before proceeding.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">4. Payment Terms</h2>
                <p className="body-text">Standard payment terms are as follows:</p>
                <ul className="legal-list">
                  <li><strong>Deposit:</strong> A deposit of up to 50% may be required upon acceptance of the quotation to secure equipment and schedule installation</li>
                  <li><strong>Progress Payment:</strong> Additional payments may be required at specified milestones for larger projects</li>
                  <li><strong>Final Payment:</strong> The remaining balance is due upon completion of installation and commissioning</li>
                  <li><strong>Payment Methods:</strong> We accept bank transfer, credit card, and other agreed payment methods</li>
                </ul>
                <p className="body-text">
                  Failure to make payment in accordance with these terms may result in suspension of work, cancellation 
                  of the contract, and/or legal action to recover amounts owed.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">5. Installation and Completion</h2>
                <p className="body-text">
                  We will make reasonable efforts to complete installation within the agreed timeframe. However, 
                  installation dates are estimates and may be affected by:
                </p>
                <ul className="legal-list">
                  <li>Weather conditions and safety considerations</li>
                  <li>Equipment availability and supply chain issues</li>
                  <li>Network connection and utility approvals</li>
                  <li>Access issues or site conditions</li>
                  <li>Regulatory approvals and inspections</li>
                </ul>
                <p className="body-text">
                  We will notify you of any significant delays and work with you to reschedule as needed. Energy Spectrum 
                  is not liable for delays caused by factors beyond our reasonable control.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">6. Customer Responsibilities</h2>
                <p className="body-text">As a customer, you agree to:</p>
                <ul className="legal-list">
                  <li>Provide accurate information about your property and energy requirements</li>
                  <li>Ensure safe and clear access to installation areas</li>
                  <li>Obtain any necessary permissions (e.g., strata approval, landlord consent)</li>
                  <li>Be present or arrange for an authorized representative during site assessments and installation</li>
                  <li>Comply with any reasonable requests from our installation team</li>
                  <li>Maintain the system in accordance with manufacturer and our recommendations</li>
                  <li>Notify us promptly of any issues or concerns with the installed system</li>
                </ul>
              </section>

              <section className="legal-section">
                <h2 className="section-title">7. Warranties</h2>
                <p className="body-text">
                  Our installations are covered by multiple warranties:
                </p>
                <ul className="legal-list">
                  <li><strong>Workmanship Warranty:</strong> We provide a warranty on our installation workmanship for a period of 5-10 years (as specified in your contract)</li>
                  <li><strong>Equipment Warranties:</strong> Solar panels, inverters, and batteries are covered by manufacturer warranties (typically 10-25 years for panels, 5-10 years for inverters, 10 years for batteries)</li>
                  <li><strong>Performance Warranties:</strong> Solar panel manufacturers typically guarantee a minimum power output over the warranty period</li>
                </ul>
                <p className="body-text">
                  Warranties may be void if the system is modified, damaged, or not maintained according to specifications. 
                  We will assist with warranty claims for equipment issues during the warranty period.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">8. Compliance and Regulations</h2>
                <p className="body-text">
                  All installations are performed in accordance with:
                </p>
                <ul className="legal-list">
                  <li>Australian Standards (AS/NZS 5033:2021 for solar installations)</li>
                  <li>Clean Energy Council (CEC) guidelines and codes of conduct</li>
                  <li>NSW electrical safety regulations</li>
                  <li>Local council requirements and building codes</li>
                  <li>Network connection standards and requirements</li>
                </ul>
                <p className="body-text">
                  Energy Spectrum holds all necessary licenses and accreditations to perform solar and battery 
                  installations in NSW. All installers are CEC-accredited professionals.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">9. Government Rebates and Incentives</h2>
                <p className="body-text">
                  We may assist with applications for government rebates and incentives, including:
                </p>
                <ul className="legal-list">
                  <li>Small-scale Technology Certificates (STCs) under the federal renewable energy scheme</li>
                  <li>NSW Government solar and battery rebate programs</li>
                  <li>Virtual Power Plant (VPP) programs</li>
                </ul>
                <p className="body-text">
                  Eligibility for rebates and incentives is determined by government agencies, not Energy Spectrum. 
                  We cannot guarantee approval or specific rebate amounts. Rebate values may change, and customers 
                  are responsible for verifying their eligibility. We will provide reasonable assistance with 
                  applications but are not liable for denied or reduced rebate claims.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">10. Limitation of Liability</h2>
                <p className="body-text">
                  To the maximum extent permitted by law:
                </p>
                <ul className="legal-list">
                  <li>Our total liability for any claim arising from our services is limited to the total amount paid by you for the specific service giving rise to the claim</li>
                  <li>We are not liable for indirect, consequential, or incidental damages, including lost profits or energy savings</li>
                  <li>We are not liable for damages caused by factors beyond our control, including extreme weather, grid failures, or third-party actions</li>
                  <li>We are not liable for performance issues caused by improper use, lack of maintenance, or unauthorized modifications</li>
                </ul>
                <p className="body-text">
                  Nothing in these Terms excludes or limits our liability for death or personal injury caused by our 
                  negligence, fraud, or any other liability that cannot be excluded under Australian Consumer Law.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">11. Australian Consumer Law</h2>
                <p className="body-text">
                  Our services come with guarantees that cannot be excluded under the Australian Consumer Law. You are 
                  entitled to a replacement or refund for a major failure and compensation for any other reasonably 
                  foreseeable loss or damage. You are also entitled to have the services supplied again or have the 
                  cost of having the services supplied again covered if the services fail to be of acceptable quality 
                  and the failure does not amount to a major failure.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">12. Cancellation and Refunds</h2>
                <p className="body-text">
                  <strong>By Customer:</strong> You may cancel your contract with us by providing written notice. 
                  Cancellation fees may apply based on work completed and equipment ordered:
                </p>
                <ul className="legal-list">
                  <li>Cancellation before equipment is ordered: Refund of deposit less $500 administrative fee</li>
                  <li>Cancellation after equipment is ordered but before installation: Refund less equipment costs and administrative fees</li>
                  <li>Cancellation during or after installation: No refund; full payment due for work completed</li>
                </ul>
                <p className="body-text">
                  <strong>By Energy Spectrum:</strong> We reserve the right to cancel or refuse service if site conditions 
                  are unsafe, property requirements cannot be met, or payment terms are not honored.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">13. Intellectual Property</h2>
                <p className="body-text">
                  All system designs, plans, and documentation provided by Energy Spectrum remain our intellectual 
                  property. You may not reproduce, distribute, or use these materials for any purpose other than the 
                  installation and maintenance of your specific system.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">14. Dispute Resolution</h2>
                <p className="body-text">
                  If a dispute arises, we encourage you to contact us first to attempt to resolve the matter informally. 
                  If we cannot reach a resolution, disputes may be referred to:
                </p>
                <ul className="legal-list">
                  <li>The Clean Energy Council's Consumer Information and Dispute Resolution service</li>
                  <li>NSW Fair Trading for consumer protection matters</li>
                  <li>Mediation or arbitration as agreed by both parties</li>
                </ul>
                <p className="body-text">
                  These Terms are governed by the laws of New South Wales, Australia, and you agree to submit to the 
                  exclusive jurisdiction of the courts of NSW.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">15. Changes to Terms</h2>
                <p className="body-text">
                  We may update these Terms from time to time. Changes will be posted on our website with a revised 
                  "Last Updated" date. Continued use of our services after changes are posted constitutes acceptance 
                  of the updated Terms. For existing contracts, the Terms in effect at the time of contract signing 
                  will apply.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">16. Contact Information</h2>
                <p className="body-text">
                  If you have questions or concerns about these Terms of Service, please contact us:
                </p>
                <div className="contact-details" style={{ marginTop: '1.5rem' }}>
                  <p className="body-text"><strong>Energy Spectrum</strong></p>
                  <p className="body-text">Leppington, NSW 2179, Australia</p>
                  <p className="body-text">Email: <a href="mailto:info@energyspectrum.com.au" className="link-primary">info@energyspectrum.com.au</a></p>
                  <p className="body-text">Phone: <a href="tel:0404844257" className="link-primary">0404 844 257</a></p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;